import React, { useState } from 'react';
import { User, Calendar, AlertTriangle, GripVertical, Building2 } from 'lucide-react';
import ScoreBadge from './ui/ScoreBadge';

const PIPELINE_STAGES = ['NEW', 'QUALIFYING', 'MEETING', 'PROPOSAL', 'NEGOTIATION'];
const CLOSED_STAGES = ['WON', 'LOST'];
const ALL_STAGES = [...PIPELINE_STAGES, ...CLOSED_STAGES];

const STAGE_COLORS = {
    NEW: { bg: 'bg-primary/5', border: 'border-primary/20', header: 'bg-primary/10 text-primary', dot: 'bg-primary' },
    QUALIFYING: { bg: 'bg-indigo-500/5', border: 'border-indigo-500/20', header: 'bg-indigo-500/10 text-indigo-500', dot: 'bg-indigo-500' },
    MEETING: { bg: 'bg-purple-500/5', border: 'border-purple-500/20', header: 'bg-purple-500/10 text-purple-500', dot: 'bg-purple-500' },
    PROPOSAL: { bg: 'bg-orange-500/5', border: 'border-orange-500/20', header: 'bg-orange-500/10 text-orange-500', dot: 'bg-orange-500' },
    NEGOTIATION: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', header: 'bg-amber-500/10 text-amber-500', dot: 'bg-amber-500' },
    WON: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', header: 'bg-emerald-500/10 text-emerald-500', dot: 'bg-emerald-500' },
    LOST: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', header: 'bg-rose-500/10 text-rose-500', dot: 'bg-rose-500' },
};

const formatCurrency = (val) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

/**
 * OpportunityKanban
 *
 * Props:
 *  - opportunities: Array of opportunity objects
 *  - onStageMove(id, newStage): async callback to persist stage change
 *  - onCardClick(id): callback to open detail view
 *  - loading: boolean
 */
export default function OpportunityKanban({ opportunities = [], onStageMove, onCardClick, loading }) {
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const [movingId, setMovingId] = useState(null);

    // Group opportunities by stage
    const grouped = {};
    ALL_STAGES.forEach(s => { grouped[s] = []; });
    opportunities.forEach(opp => {
        if (grouped[opp.stage]) {
            grouped[opp.stage].push(opp);
        }
    });

    // Drag Handlers
    const handleDragStart = (e, oppId) => {
        setDraggedId(oppId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', oppId);
    };

    const handleDragOver = (e, stage) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverStage(stage);
    };

    const handleDragLeave = () => {
        setDragOverStage(null);
    };

    const handleDrop = async (e, targetStage) => {
        e.preventDefault();
        setDragOverStage(null);
        const oppId = e.dataTransfer.getData('text/plain') || draggedId;
        if (!oppId) return;

        const opp = opportunities.find(o => o._id === oppId);
        if (!opp || opp.stage === targetStage) {
            setDraggedId(null);
            return;
        }

        setMovingId(oppId);
        try {
            await onStageMove(oppId, targetStage);
        } finally {
            setMovingId(null);
            setDraggedId(null);
        }
    };

    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverStage(null);
    };

    const isOverdue = (opp) => {
        if (!opp.expectedCloseDate) return false;
        return new Date(opp.expectedCloseDate) < new Date() && opp.status === 'OPEN';
    };

    if (loading) {
        return (
            <div className="flex gap-4 overflow-x-auto pb-4">
                {PIPELINE_STAGES.map(s => (
                    <div key={s} className="min-w-[240px] bg-gray-50 rounded-xl border border-gray-200 p-4 animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-24 mb-4"></div>
                        <div className="space-y-3">
                            <div className="h-20 bg-gray-100 rounded-lg"></div>
                            <div className="h-20 bg-gray-100 rounded-lg"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
            {ALL_STAGES.map(stage => {
                const stageOpps = grouped[stage];
                const colors = STAGE_COLORS[stage];
                const totalValue = stageOpps.reduce((sum, o) => sum + (o.expectedValue || 0), 0);
                const isDropTarget = dragOverStage === stage && draggedId;

                return (
                    <div
                        key={stage}
                        className={`min-w-[280px] max-w-[320px] flex-shrink-0 rounded-3xl border-2 transition-all duration-200 flex flex-col overflow-hidden ${isDropTarget ? `${colors.border} shadow-lg scale-[1.01]` : 'border-border/40'
                            } ${colors.bg}`}
                        onDragOver={(e) => handleDragOver(e, stage)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, stage)}
                    >
                        {/* Column Header */}
                        <div className={`px-5 py-4 ${colors.header} border-b border-white/10 flex items-center justify-between`}>
                            <div className="flex items-center gap-2.5">
                                <div className={`w-2 h-2 rounded-full ${colors.dot} shadow-[0_0_8px_rgba(0,0,0,0.1)]`}></div>
                                <span className="text-[10px] font-black uppercase tracking-[0.15em]">{stage}</span>
                            </div>
                            <span className="text-[10px] font-black bg-white/20 px-2.5 py-1 rounded-lg backdrop-blur-sm">
                                {stageOpps.length}
                            </span>
                        </div>

                        {/* Column Summary */}
                        <div className="px-5 py-3 border-b border-dashed border-border/50 text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex justify-between bg-white/5">
                            <span className="tabular-nums text-foreground">{formatCurrency(totalValue)}</span>
                            <span>{stageOpps.length} Deal{stageOpps.length !== 1 ? 's' : ''}</span>
                        </div>

                        {/* Cards */}
                        <div className="flex-1 p-3 space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                            {stageOpps.length === 0 ? (
                                <div className="text-center py-12 text-[10px] text-muted-foreground font-black uppercase tracking-widest opacity-40">
                                    {isDropTarget ? 'Release to drop' : 'No Active Deals'}
                                </div>
                            ) : stageOpps.map(opp => (
                                <div
                                    key={opp._id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, opp._id)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => onCardClick && onCardClick(opp._id)}
                                    className={`bg-card rounded-2xl border shadow-sm p-4 cursor-grab active:cursor-grabbing hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group ring-primary/20 hover:ring-2 ${draggedId === opp._id ? 'opacity-40 scale-95' : ''
                                        } ${movingId === opp._id ? 'animate-pulse border-primary shadow-lg ring-4' : 'border-border/60 hover:border-primary/30'}`}
                                >
                                    {/* Card Header */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex flex-col gap-1.5 flex-1 mr-2">
                                            <h4 className="text-sm font-black text-foreground leading-snug tracking-tight group-hover:text-primary transition-colors line-clamp-2">
                                                {opp.title}
                                            </h4>
                                        </div>
                                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground opacity-20 group-hover:opacity-100 flex-shrink-0 mt-0.5 transition-opacity" />
                                    </div>

                                    {/* Company */}
                                    {(opp.companyId?.name || opp.companyMasterId?.name) && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-4 opacity-70 group-hover:opacity-100 transition-opacity">
                                            <Building2 className="w-3 h-3" />
                                            <span className="truncate">{opp.companyId?.name || opp.companyMasterId?.name}</span>
                                        </div>
                                    )}

                                    {/* Value & Score */}
                                    <div className="flex items-center justify-between gap-1 mb-4">
                                        <div className="flex flex-col">
                                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-50 mb-0.5">Value</span>
                                            <div className="flex items-center gap-1 text-base font-black text-foreground tabular-nums">
                                                {formatCurrency(opp.expectedValue)}
                                            </div>
                                        </div>
                                        {opp.intelligence && (
                                            <div className="scale-90 origin-right">
                                                <ScoreBadge intelligence={opp.intelligence} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer Meta */}
                                    <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-3 border-t border-border/50">
                                        <div className="flex items-center gap-1.5 font-bold uppercase tracking-widest opacity-60">
                                            <User className="w-3 h-3" />
                                            <span className="truncate max-w-[80px]">{opp.ownerId?.name || opp.ownerId?.email || 'Unassigned'}</span>
                                        </div>
                                        {opp.expectedCloseDate && (
                                            <div className={`flex items-center gap-1.5 font-black uppercase tracking-widest ${isOverdue(opp) ? 'text-red-500' : 'opacity-60'}`}>
                                                {isOverdue(opp) && <AlertTriangle className="w-3 h-3" />}
                                                <Calendar className="w-3 h-3" />
                                                <span>{new Date(opp.expectedCloseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
